import React, { useMemo } from 'react';
import { Activity, Clipboard, Thermometer } from 'lucide-react';
import { CalibrationPointsConfig } from '../../sensors/types';
import { buildFrequencyRows, buildTemperatureRows } from '../utils/buildFrequencyTemperatureRows';
import { FrequencyTemperatureTable } from '../FrequencyTemperatureTable';

interface FrequencyTemperatureStepProps {
  individualCalibrationData: any;
  setIndividualCalibrationData: React.Dispatch<React.SetStateAction<any>>;

  schemaConfig: {
    calibrationPoints: CalibrationPointsConfig;
    generatePlaceholder: (point: number, sequence?: 'inc' | 'dec' | 'inc2') => string;
  };

  onClearFrequencyFields: () => void;
  onClearTemperatureFields: () => void;
}

export const FrequencyTemperatureStep: React.FC<FrequencyTemperatureStepProps> = ({
  individualCalibrationData,
  setIndividualCalibrationData,
  schemaConfig,
  onClearFrequencyFields,
  onClearTemperatureFields,
}) => {
  const frequencyRows = useMemo(() => buildFrequencyRows(schemaConfig), [schemaConfig]);
  const temperatureRows = useMemo(() => buildTemperatureRows(), []);

  return (
    <div className="space-y-4">
      <div>
        <h4 className="flex items-center gap-2 text-base font-semibold text-blue-800">
          <Clipboard className="h-4 w-4" />
          Quick Fill - Paste All Data at Once
        </h4>
        <p className="mt-1 text-sm text-gray-500">
          Paste your data below — we'll automatically place values next to the correct frequencies and temperature
          points.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <FrequencyTemperatureTable
          icon={<Activity className="h-4 w-4 text-blue-600" />}
          title="Frequency Data"
          subtitle="Paste frequency values (e.g., BOL, AOL, 10, 20…)"
          headerLabel="Frequency"
          headerAccent="bg-blue-50"
          rows={frequencyRows}
          individualCalibrationData={individualCalibrationData}
          setIndividualCalibrationData={setIndividualCalibrationData}
          onClearFields={onClearFrequencyFields}
        />
        <FrequencyTemperatureTable
          icon={<Thermometer className="h-4 w-4 text-red-500" />}
          title="Temperature Data"
          subtitle="Paste temperature values (Room T, High T, Room F…)"
          headerLabel="Temperature Point"
          headerAccent="bg-red-50"
          rows={temperatureRows}
          individualCalibrationData={individualCalibrationData}
          setIndividualCalibrationData={setIndividualCalibrationData}
          onClearFields={onClearTemperatureFields}
        />
      </div>
    </div>
  );
};
